import {Injectable} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {DataClientError, DisplayableDataServerMessage, ErrorService, ValidationMessage} from "@fedeltapointofsale/data-client";
import {FUIAlertService, FUISpinnersService} from "@fedeltapointofsale/ui";
import {AnalyticsService} from "./analytics/analytics.service";
import {Subject} from "rxjs";

/**
 * Service to handle errors
 */
@Injectable()
export class ErrorHandlerService {
    private showingError: boolean;

    public resourceNotFound$: Subject<DisplayableDataServerMessage> = new Subject<DisplayableDataServerMessage>();

    constructor(private errorService: ErrorService,
                private translate: TranslateService,
                private alertService: FUIAlertService,
                private spinnersService: FUISpinnersService,
                private analyticsService: AnalyticsService) {
        this.errorService.errors.subscribe((error: DataClientError | any) => {
            if (error instanceof DataClientError) {
                this.displayDataClientError(error);
            } else {
                console.error(error);
                if (error["message"] === "setPhotoOptions failed" || error["message"] === "The associated Track is in an invalid state") {
                    console.error("\terror ignored");
                } else {
                    this.displayError("wbo.common.error.unknown");
                }
            }
        });
        this.errorService.dataClientErrors.subscribe((error: DataClientError) => {
            this.displayDataClientError(error);
        });
    }

    /**
     * Displays an error to the user
     * @param key i18n key from the resource bundle. See src/assets/i18n/en.json
     * @param interpolateParams
     */
    displayError(key: string, interpolateParams?: Object) {
        this.analyticsService.trackException(key ? key : "wbo.common.error.unknown");

        if (!this.showingError) {
            if (this.spinnersService.isOpen()) {
                this.spinnersService.close();
            }
            this.showingError = true;

            this.alertService.showError(undefined, key || "wbo.common.error.unknown", interpolateParams).subscribe(() => {
                this.showingError = false;
            });
        }
    }

    /**
     * Displays a translated DataClientError to the user.
     * @param error DataClientError
     * @param fallback Optional i18n key to use if the DataClientError cannot be resolved to a meaningful message
     */
    async displayDataClientError(error: DataClientError, fallback?: string): Promise<void> {
        const displayableMessage: DisplayableDataServerMessage = await error.displayableMessage;
        const validationMessages: ValidationMessage[] = await error.validationMessages;
        if (displayableMessage) {
            if (displayableMessage.messageKey.startsWith("server.exception.resource.notFound.") && this.resourceNotFound$.observers.length) {
                this.resourceNotFound$.next(displayableMessage);
            } else {
                this.displayError(displayableMessage.messageKey, displayableMessage.messageParams);
            }
        } else if (validationMessages && validationMessages[0]) {
            this.displayError(validationMessages[0].messageKey, validationMessages[0].messageParams);
        } else if (error.status === 404) {
            this.displayError("wbo.common.error.notFound");
        } else if (error.status === 0 || error.status === 504) {
            this.displayError("wbo.common.error.noConnection");
        }
        // it will be handled by the versionService requiredVersion
        else if (error.status === 406) {
            console.error(error);
        } else if (fallback) {
            console.error(error);
            this.displayError(fallback);
        } else {
            console.error(error);
            this.displayError("wbo.common.error.unknown");
        }
    }
}
